import { inject } from '@angular/core';
import { Router } from '@angular/router';

import { signalStore, withMethods, withProps, withState } from '@ngrx/signals';

import {
  CardPermissions,
  OrganizationsPermissions,
  PermissionCollection,
  PermissionConditionsNames,
  Permissions,
  PermissionsNames,
  Role,
  SettingsPermissions,
  TransactionPermissions,
  UsersPermissions,
  WalletsPermissions,
} from '@pp/core/entities';

import { StorageStore } from './index';

interface PermissionsState {
  readonlyRights: boolean;
  WALLETS_PAGE_PERMISSION_COLLECTION: [PermissionsNames, PermissionConditionsNames];
  ACTIVITY_PAGE_PERMISSION_COLLECTION: [PermissionsNames, PermissionConditionsNames];
  ORGANIZATIONS_PAGE_PERMISSION_COLLECTION: [PermissionsNames, PermissionConditionsNames];
  USERS_PAGE_PERMISSION_COLLECTION: [PermissionsNames, PermissionConditionsNames];
  CARDS_PAGE_PERMISSION_COLLECTION: [PermissionsNames, PermissionConditionsNames];
}

const initialState: PermissionsState = {
  readonlyRights: false,
  WALLETS_PAGE_PERMISSION_COLLECTION: [PermissionsNames.Wallets, PermissionConditionsNames.Wallets],
  ACTIVITY_PAGE_PERMISSION_COLLECTION: [PermissionsNames.Activity, PermissionConditionsNames.Activity],
  ORGANIZATIONS_PAGE_PERMISSION_COLLECTION: [PermissionsNames.Organizations, PermissionConditionsNames.Organizations],
  USERS_PAGE_PERMISSION_COLLECTION: [PermissionsNames.Users, PermissionConditionsNames.Users],
  CARDS_PAGE_PERMISSION_COLLECTION: [PermissionsNames.Cards, PermissionConditionsNames.Cards],
};

export const PermissionsStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withProps(() => ({
    router: inject(Router),
    storageStore: inject(StorageStore),
  })),
  withProps(store => ({
    role: store.storageStore.role,
    permissions: store.storageStore.permissions,
  })),
  withMethods(() => ({
    isPageVisible(collection: PermissionCollection, permissions: Map<PermissionsNames, any>): boolean {
      const [permissionName, conditionName] = collection;
      const permission = permissions?.get(permissionName);
      return permission ? !!permission[conditionName] : false;
    },
  })),
  withMethods(store => ({
    canSeePage(permissionCollection: PermissionCollection, withRedirect = true): boolean {
      const canSee = store.isPageVisible(permissionCollection, store.permissions());

      if (!canSee && withRedirect) {
        store.router.navigate(['403']);
      }

      return canSee;
    },

    canSeePageByRoles(roles: Role[], withRedirect = true): boolean {
      const canSee = roles.includes(store.role());

      if (!canSee && withRedirect) {
        store.router.navigate(['403']);
      }

      return canSee;
    },

    handleDefaultPage(permissions: Permissions): void {
      if (!permissions) {
        return;
      }

      const redirect = (path: string) => store.router.navigate([path]);

      if (store.isPageVisible(store.WALLETS_PAGE_PERMISSION_COLLECTION(), permissions)) {
        redirect('wallets');
      } else if (store.isPageVisible(store.ACTIVITY_PAGE_PERMISSION_COLLECTION(), permissions)) {
        redirect('activity');
      } else if (store.isPageVisible(store.ORGANIZATIONS_PAGE_PERMISSION_COLLECTION(), permissions)) {
        redirect('teams');
      } else if (store.isPageVisible(store.USERS_PAGE_PERMISSION_COLLECTION(), permissions)) {
        redirect('users');
      } else if (store.isPageVisible(store.CARDS_PAGE_PERMISSION_COLLECTION(), permissions)) {
        redirect('cards');
      } else {
        redirect('403');
      }
    },
  })),
  withMethods(() => ({
    getPermissionByName<T>(permissions: Permissions, permissionName: PermissionsNames): T {
      return permissions?.has(permissionName) && (permissions.get(permissionName) as T);
    },
  })),
  withMethods(store => ({
    getPagePermissions<T>(permissionName: PermissionsNames): T | null {
      const res = store.permissions();
      return store.getPermissionByName<T>(res, permissionName) || null;
    },
  })),
  withMethods(store => ({
    isWalletsVisible(): boolean {
      return store.canSeePage(store.WALLETS_PAGE_PERMISSION_COLLECTION(), false);
    },

    isActivityVisible(): boolean {
      return store.canSeePage(store.ACTIVITY_PAGE_PERMISSION_COLLECTION(), false);
    },

    isOrganizationsVisible(): boolean {
      const role = store.role();
      return role === Role.SuperAdmin || false;
    },

    isUsersVisible(): boolean {
      return store.canSeePage(store.USERS_PAGE_PERMISSION_COLLECTION(), false);
    },

    isCardsVisible(): boolean {
      return store.canSeePage(store.CARDS_PAGE_PERMISSION_COLLECTION(), false);
    },

    isPromoVisible(): boolean {
      return store.canSeePageByRoles([Role.SuperAdmin], false);
    },

    isPayOrdersVisible(): boolean {
      return store.canSeePageByRoles([Role.Admin], false);
    },

    isAgentAccountsVisible(): boolean {
      return store.canSeePageByRoles([Role.Admin, Role.Supervisor], false);
    },

    walletsPermissions(): WalletsPermissions {
      return store.getPagePermissions<WalletsPermissions>(PermissionsNames.Wallets);
    },

    transactionPermissions(): TransactionPermissions {
      return store.getPagePermissions<TransactionPermissions>(PermissionsNames.Activity);
    },

    organizationsPermissions(): OrganizationsPermissions {
      return store.getPagePermissions<OrganizationsPermissions>(PermissionsNames.Organizations);
    },

    usersPermissions(): UsersPermissions {
      return store.getPagePermissions<UsersPermissions>(PermissionsNames.Users);
    },

    settingsPermissions(): SettingsPermissions {
      return store.getPagePermissions<SettingsPermissions>(PermissionsNames.Settings);
    },

    cardsPermissions(): CardPermissions {
      return store.getPagePermissions<CardPermissions>(PermissionsNames.Cards);
    },

    redirectTo403(): void {
      store.router.navigate(['403']);
    },
  })),
);
